import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ArrowBackRounded,
  ChatRounded,
  FlagRounded,
  AutoAwesomeRounded,
  FileDownloadRounded,
  ScheduleRounded,
  CheckCircleRounded,
  TrackChangesRounded,
  ArrowUpwardRounded,
  ArrowDownwardRounded,
} from '@mui/icons-material';
import {
  ReinforcementStudent,
  ReinforcementStudentTopic,
  ReinforcementTopicState,
  Student,
  DashboardStudentSnapshot,
  ConversationWithMessages,
  StudentDailyRecord,
} from '../types';
import {
  getClassReinforcementReport,
  getClassDashboardMetrics,
  getStudentTopicCompletions,
  getStudentConversations,
  getStudentDailyMetrics,
  getReinforcementFlags,
  createReinforcementFlags,
  resolveReinforcementFlag,
  analyzeStudentConversations,
  ReinforcementFlag,
  StudentTopicProgress,
  StudentTopicCompletion,
} from '../services/api';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import { resolveAnalysisText } from '../utils/analysisText';
import { writeSummarySection } from '../utils/reportPdf';
import { resolveTeacherClassContext } from '../utils/teacherClassContext';
import { computeStudentScore } from '../utils/studentScore';
import TeacherLayout from './teacher/TeacherLayout';
import DateRangeControl, { DateRange } from './teacher/DateRangeControl';

// Plain-language summary of how a student is doing, keyed off the category.
const CATEGORY_META: Record<
  ReinforcementStudent['category'],
  { flag: string; tone: string; ring: string; headline: (n: string) => string }
> = {
  bright: {
    flag: 'Doing great',
    tone: 'bg-emerald-100 text-emerald-800',
    ring: '#10b981',
    headline: (n) => `${n} is doing great — picking up new ideas quickly.`,
  },
  hardworking: {
    flag: 'On track',
    tone: 'bg-sky-100 text-sky-800',
    ring: '#0ea5e9',
    headline: (n) => `${n} is doing well — gets there with a little practice.`,
  },
  disengaged: {
    flag: 'Needs help',
    tone: 'bg-amber-100 text-amber-800',
    ring: '#f59e0b',
    headline: (n) => `${n} needs some support with the basics right now.`,
  },
  unknown: {
    flag: 'No data yet',
    tone: 'bg-slate-100 text-slate-600',
    ring: '#cbd5e1',
    headline: (n) => `Not enough chats yet to tell how ${n} is doing.`,
  },
};

// Simple, parent-friendly words for a topic's mastery state.
const TOPIC_STATE_META: Record<ReinforcementTopicState, { tone: string; label: string }> = {
  solid: { tone: 'bg-emerald-100 text-emerald-700', label: 'Got it' },
  partly_solid: { tone: 'bg-amber-100 text-amber-700', label: 'Almost' },
  forgotten: { tone: 'bg-rose-100 text-rose-700', label: 'Needs practice' },
  never_learned: { tone: 'bg-rose-100 text-rose-700', label: 'Needs practice' },
};

// Reports are LLM-generated, so a topic's state can occasionally arrive
// missing or off-spec. Fall back to a neutral chip instead of crashing.
const TOPIC_STATE_FALLBACK = { tone: 'bg-slate-100 text-slate-600', label: 'In progress' };
const topicStateMeta = (state: ReinforcementTopicState) =>
  TOPIC_STATE_META[state] ?? TOPIC_STATE_FALLBACK;

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
  global: 'Your topics',
};

// Normalize a topic/chapter name so an understanding topic (a chat theme) and a
// finished syllabus chapter that mean the same thing line up: lowercase, drop a
// leading "3.2 " style number, collapse whitespace, strip punctuation.
const normalizeTopic = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/^\d+(?:\.\d+)*\s+/, '')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const avatarInitials = (name: string) =>
  name.trim().split(/\s+/).map((w) => w[0] ?? '').slice(0, 2).join('').toUpperCase() || '?';

const formatMinutes = (m?: number | null): string => {
  if (m == null || Number.isNaN(m)) return '—';
  const total = Math.round(m);
  const h = Math.floor(total / 60);
  const min = total % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

// Monday (local) of the ISO week for a 'YYYY-MM-DD' date string. Timezone-safe
// (built from parts, not Date-parsed) so week boundaries don't drift.
const formatWeekLabel = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const num = (v?: number | null): string => (v == null ? '—' : String(Math.round(v)));

interface StudentReportState {
  student?: Student;
  school?: string;
  grade?: number | string;
  section?: string | null;
}

const StudentReport: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useTeacherAuth();
  const state = (location.state as StudentReportState) || {};
  const student = state.student;

  const classContext = resolveTeacherClassContext(state);
  const gradeNum =
    typeof classContext.grade === 'string' ? Number(classContext.grade) : classContext.grade;
  const backState = {
    school: classContext.school,
    grade: classContext.grade,
    section: classContext.section ?? undefined,
  };

  const [range, setRange] = useState<DateRange>({ start: '', end: '' });
  const [reportStudent, setReportStudent] = useState<ReinforcementStudent | null>(null);
  const [snapshot, setSnapshot] = useState<DashboardStudentSnapshot | null>(null);
  const [topicProgress, setTopicProgress] = useState<StudentTopicProgress[]>([]);
  const [completions, setCompletions] = useState<StudentTopicCompletion[]>([]);
  const [conversations, setConversations] = useState<ConversationWithMessages[]>([]);
  const [daily, setDaily] = useState<StudentDailyRecord[]>([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!token || !student || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    let on = true;
    getClassReinforcementReport(
      token, Number(gradeNum), classContext.section, false,
      range.start || undefined, range.end || undefined, classContext.school
    )
      .then((r) => {
        if (!on) return;
        const rows = r.report_data?.students ?? [];
        setReportStudent(
          rows.find((s) => s.student_id === student.id) ??
            rows.find((s) => s.name === student.first_name) ?? null
        );
      })
      .catch(() => on && setReportStudent(null));
    return () => { on = false; };
  }, [token, student, gradeNum, classContext.school, classContext.section, range.start, range.end]);

  useEffect(() => {
    if (!student || !classContext.school || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    let on = true;
    getClassDashboardMetrics(
      classContext.school, Number(gradeNum), classContext.section,
      range.start || undefined, range.end || undefined
    )
      .then((r) => on && setSnapshot((r.student_snapshots ?? []).find((s) => s.student_id === student.id) ?? null))
      .catch(() => on && setSnapshot(null));
    getStudentDailyMetrics(
      classContext.school, Number(gradeNum), [student.id], classContext.section,
      range.start || undefined, range.end || undefined
    )
      .then((r) => on && setDaily((r.students?.[0]?.records ?? [])))
      .catch(() => on && setDaily([]));
    return () => { on = false; };
  }, [student, classContext.school, gradeNum, classContext.section, range.start, range.end]);

  useEffect(() => {
    if (!token || !student) return;
    let on = true;
    getStudentTopicCompletions(token, student.id)
      .then((r) => {
        if (!on) return;
        setTopicProgress(r.progress || []);
        setCompletions(r.completions || []);
      })
      .catch(() => {
        if (!on) return;
        setTopicProgress([]);
        setCompletions([]);
      });
    getStudentConversations(student.id, 5)
      .then((r) => on && setConversations(r.conversations || []))
      .catch(() => on && setConversations([]));
    return () => { on = false; };
  }, [token, student]);

  const category = reportStudent?.category ?? 'unknown';
  const catMeta = CATEGORY_META[category];

  const strengths = useMemo(
    () => (reportStudent?.topics ?? []).filter((t) => t.state === 'solid'),
    [reportStudent]
  );
  const weakTopics = useMemo(
    () => (reportStudent?.topics ?? []).filter((t) => t.struggled),
    [reportStudent]
  );

  // Topics grouped by subject; each keeps whether it came from the pushed
  // curriculum (vs. free chat). Known subjects first, "Other" last.
  const topicsBySubject = useMemo<[string, ReinforcementStudentTopic[]][]>(() => {
    const map = new Map<string, ReinforcementStudentTopic[]>();
    (reportStudent?.topics ?? []).forEach((t) => {
      const key = t.subject ? SUBJECT_LABELS[t.subject] || t.subject : 'Other';
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    });
    const order = ['Maths', 'Science', 'Social', 'Your topics'];
    return Array.from(map.entries()).sort((a, b) => {
      const ai = order.indexOf(a[0]);
      const bi = order.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [reportStudent]);

  // Mastery mix for the donut: understood / almost / needs-practice.
  const masteryMix = useMemo(() => {
    const c = { understood: 0, almost: 0, needs: 0 };
    (reportStudent?.topics ?? []).forEach((t) => {
      if (t.state === 'solid') c.understood += 1;
      else if (t.state === 'partly_solid') c.almost += 1;
      else c.needs += 1;
    });
    return c;
  }, [reportStudent]);

  // Daily activity, chronological — feeds the progress chart and the trend deltas.
  const dailySorted = useMemo(
    () => [...daily].filter((d) => d.day).sort((a, b) => (String(a.day) < String(b.day) ? -1 : 1)),
    [daily]
  );
  const totalMinutes = useMemo(
    () => dailySorted.reduce((a, d) => a + (d.minutes_spent ?? 0), 0),
    [dailySorted]
  );
  const totalMessages = useMemo(
    () => dailySorted.reduce((a, d) => a + (d.user_messages ?? 0), 0),
    [dailySorted]
  );

  // Trend: last 7 days of data vs the 7 days before (for the "vs last 7d" deltas).
  const trend = useMemo(() => {
    const toT = (s: string) => {
      const [y, m, d] = s.slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d).getTime();
    };
    if (!dailySorted.length) return { minUp: null as boolean | null, msgUp: null as boolean | null };
    const end = toT(String(dailySorted[dailySorted.length - 1].day));
    const DAY = 86400000;
    let curMin = 0, prevMin = 0, curMsg = 0, prevMsg = 0;
    dailySorted.forEach((d) => {
      const t = toT(String(d.day));
      if (t > end - 7 * DAY) { curMin += d.minutes_spent ?? 0; curMsg += d.user_messages ?? 0; }
      else if (t > end - 14 * DAY) { prevMin += d.minutes_spent ?? 0; prevMsg += d.user_messages ?? 0; }
    });
    return {
      minUp: curMin === prevMin ? null : curMin > prevMin,
      msgUp: curMsg === prevMsg ? null : curMsg > prevMsg,
    };
  }, [dailySorted]);

  // Overall understanding % — from finished/total topics; falls back to the
  // mastery mix (solid = 1, almost = 0.5) when no completion data exists.
  const understandingPct = useMemo<number | null>(() => {
    const totFinished = topicProgress.reduce((a, p) => a + p.finished, 0);
    const totAll = topicProgress.reduce((a, p) => a + p.total, 0);
    if (totAll > 0) return Math.round((totFinished / totAll) * 100);
    const topics = reportStudent?.topics ?? [];
    if (topics.length === 0) return null;
    const score = topics.reduce(
      (a, t) => a + (t.state === 'solid' ? 1 : t.state === 'partly_solid' ? 0.5 : 0),
      0
    );
    return Math.round((score / topics.length) * 100);
  }, [topicProgress, reportStudent]);

  const curiosityScore = useMemo(() => {
    const vals = conversations
      .map((c) => c.curiosity_summary?.average)
      .filter((v): v is number => typeof v === 'number');
    if (vals.length === 0) return null;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }, [conversations]);

  // Best quiz score per topic, keyed by normalized name, so a finished
  // syllabus chapter's quiz lines up with the matching understanding topic.
  // A repeat pass keeps the higher score.
  const quizByTopic = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    completions.forEach((c) => {
      if (c.quiz_score == null) return;
      const key = normalizeTopic(c.chapter_title || '');
      if (!key) return;
      map.set(key, Math.max(map.get(key) ?? 0, c.quiz_score));
    });
    return map;
  }, [completions]);

  // Quiz average from this student's completed-topic quizzes — a local fallback
  // for the report's `quiz_avg` field (identical formula, so they agree).
  const localQuizAvg = useMemo<number | null>(() => {
    const scores = completions
      .map((c) => c.quiz_score)
      .filter((v): v is number => typeof v === 'number');
    if (scores.length === 0) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [completions]);

  // Overall evaluation = the SAME shared blend the dashboard leaderboard uses.
  // Prefer the report's backend-computed averages (consistent across the whole
  // class) and fall back to the locally-derived ones.
  const evaluation = useMemo(
    () =>
      computeStudentScore({
        topics: reportStudent?.topics ?? [],
        quizAvg: reportStudent?.quiz_avg ?? localQuizAvg,
        curiosityAvg: reportStudent?.curiosity_avg ?? curiosityScore,
      }),
    [reportStudent, localQuizAvg, curiosityScore]
  );
  const overallScore = evaluation.score;
  const quizPct = evaluation.quiz;

  // A combined per-topic row (understanding state + depth + quiz score) grouped
  // by subject, for the detailed "Topic by topic" breakdown. Kept separate from
  // `topicsBySubject` above, which stays a plain topic list for the chip view.
  type CombinedTopic = {
    topic: string;
    state: ReinforcementTopicState;
    depth: number | null;
    quiz: number | null;
  };
  const topicRowsBySubject = useMemo<[string, CombinedTopic[]][]>(() => {
    const map = new Map<string, CombinedTopic[]>();
    (reportStudent?.topics ?? []).forEach((t) => {
      const key = t.subject ? SUBJECT_LABELS[t.subject] || t.subject : 'Other';
      const arr = map.get(key) ?? [];
      arr.push({
        topic: t.topic,
        state: t.state,
        depth: t.depth,
        quiz: quizByTopic.get(normalizeTopic(t.topic)) ?? null,
      });
      map.set(key, arr);
    });
    return Array.from(map.entries());
  }, [reportStudent, quizByTopic]);


  // ── Reinforcement flags (merged in from Student Analysis) ──────────────────
  const flagRef = useRef<HTMLDivElement>(null);
  const [flags, setFlags] = useState<ReinforcementFlag[]>([]);
  const [flagTopic, setFlagTopic] = useState('');
  const [flagCustomTopic, setFlagCustomTopic] = useState('');
  const [flagNote, setFlagNote] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagError, setFlagError] = useState<string | null>(null);
  const [flagConfirm, setFlagConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !student || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    let on = true;
    getReinforcementFlags(token, Number(gradeNum), classContext.section)
      .then((r) => on && setFlags((r.flags ?? []).filter((f) => f.student_id === student.id)))
      .catch(() => on && setFlags([]));
    return () => { on = false; };
  }, [token, student, gradeNum, classContext.section]);

  // Weakest topics first, so the most useful ones to reinforce are at the top.
  const flagTopicOptions = useMemo(() => {
    const order: Record<ReinforcementTopicState, number> = {
      never_learned: 0, forgotten: 1, partly_solid: 2, solid: 3,
    };
    return [...(reportStudent?.topics ?? [])].sort((a, b) => order[a.state] - order[b.state]);
  }, [reportStudent]);

  const usingCustomTopic = flagTopic === '__custom__';
  const effectiveTopic = (usingCustomTopic ? flagCustomTopic : flagTopic).trim();

  const handleFlagSubmit = useCallback(async () => {
    if (!token || !student || gradeNum == null) return;
    const topic = effectiveTopic;
    if (!topic) { setFlagError('Pick a topic or type one in.'); return; }
    const matched = (reportStudent?.topics ?? []).find((t) => t.topic === topic);
    setFlagSubmitting(true); setFlagError(null);
    try {
      const res = await createReinforcementFlags(token, {
        grade: Number(gradeNum),
        section: classContext.section,
        student_ids: [student.id],
        topic,
        subject: matched?.subject ?? null,
        note: flagNote.trim() || null,
      });
      setFlags((prev) => [...(res.flags ?? []), ...prev]);
      setFlagTopic(''); setFlagCustomTopic(''); setFlagNote('');
      setFlagConfirm(`Flagged "${topic}" — the curiosity will bring it up next time ${student.first_name} chats.`);
    } catch (err) {
      setFlagError(err instanceof Error ? err.message : 'Could not flag this topic. Try again.');
    } finally {
      setFlagSubmitting(false);
    }
  }, [token, student, gradeNum, classContext.section, effectiveTopic, flagNote, reportStudent]);

  const handleClearFlag = useCallback(async (id: number) => {
    if (!token) return;
    const prev = flags;
    setFlags((cur) => cur.filter((f) => f.id !== id));
    try {
      await resolveReinforcementFlag(token, id);
    } catch {
      setFlags(prev);
      setFlagError('Could not clear that flag. Try again.');
    }
  }, [token, flags]);

  if (!student) {
    return (
      <TeacherLayout active="classes">
        <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">
          Pick a student from the Classes screen to see their report.
          <div className="mt-4">
            <button
              type="button"
              onClick={() => navigate('/class-details', { state: backState })}
              className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
            >
              Go to Classes
            </button>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  const goWithStudent = (path: string) => navigate(path, { state: { student, ...backState } });

  const handleDownload = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Generate the LLM narrative first (may queue a job and poll to completion).
      let narrative: string | null = null;
      try {
        narrative = await resolveAnalysisText(() => analyzeStudentConversations(student.id));
      } catch {
        narrative = null;
      }

      const doc = new jsPDF();
      const mx = 14;
      const contentW = 182;
      let y = 18;
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(`Student Report — ${student.first_name}`, mx, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120);
      const meta = [
        classContext.school,
        gradeNum != null && !Number.isNaN(Number(gradeNum)) ? `Grade ${gradeNum}` : null,
        classContext.section ? `Section ${classContext.section}` : null,
        `Roll ${student.roll_number}`,
      ]
        .filter(Boolean)
        .join('  ·  ');
      doc.text(meta, mx, y);
      y += 9;

      // Written summary — the LLM narrative for this student, or a plain-language
      // fallback built from the stats when the narrative isn't available.
      const fallbackParts: string[] = [catMeta.headline(student.first_name)];
      if (overallScore != null) {
        fallbackParts.push(`Overall evaluation is around ${overallScore}/100.`);
      }
      if (understandingPct != null) {
        fallbackParts.push(`Overall understanding is around ${understandingPct}%.`);
      }
      if (strengths.length > 0) {
        fallbackParts.push(`Doing well in: ${strengths.slice(0, 5).map((t) => t.topic).join(', ')}.`);
      }
      if (weakTopics.length > 0) {
        fallbackParts.push(`Needs help with: ${weakTopics.slice(0, 5).map((t) => t.topic).join(', ')}.`);
      }
      y = writeSummarySection(doc, 'Summary', narrative || fallbackParts.join(' '), mx, y, contentW);

      autoTable(doc, {
        startY: y,
        head: [['Overall', 'Understanding', 'Quiz avg', 'Curiosity', 'Questions', 'Time', 'Focus / chat']],
        body: [[
          overallScore != null ? `${overallScore}/100` : '—',
          understandingPct != null ? `${understandingPct}%` : '—',
          quizPct != null ? `${quizPct}/100` : '—',
          curiosityScore != null ? `${curiosityScore}/100` : '—',
          num(snapshot?.total_user_messages),
          formatMinutes(snapshot?.total_minutes),
          snapshot?.avg_attention_span != null ? `${Math.round(snapshot.avg_attention_span)}m` : '—',
        ]],
        theme: 'grid',
        headStyles: { fillColor: [124, 58, 237] },
        styles: { fontSize: 9, halign: 'center' },
      });
      y = (doc as any).lastAutoTable.finalY + 8;

      if ((reportStudent?.topics ?? []).length > 0) {
        autoTable(doc, {
          startY: y,
          head: [['Topic', 'Subject', 'Understanding', 'Quiz']],
          body: (reportStudent?.topics ?? []).map((t) => {
            const quiz = quizByTopic.get(normalizeTopic(t.topic));
            return [
              t.topic,
              t.subject ? SUBJECT_LABELS[t.subject] || t.subject : '—',
              topicStateMeta(t.state).label,
              quiz != null ? `${quiz}` : '—',
            ];
          }),
          theme: 'striped',
          headStyles: { fillColor: [124, 58, 237] },
          styles: { fontSize: 9 },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      const recs = [
        ...weakTopics.slice(0, 4).map((t) => `Revisit ${t.topic}`),
        ...(reportStudent?.gaps ?? []).slice(0, 3).map((g) => `Close the gap: ${g}`),
      ];
      if (recs.length > 0) {
        autoTable(doc, {
          startY: y,
          head: [['Recommended next steps']],
          body: recs.map((r) => [r]),
          theme: 'plain',
          headStyles: { fontStyle: 'bold', textColor: [124, 58, 237] },
          styles: { fontSize: 10 },
        });
      }

      doc.save(`Student report - ${student.first_name}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <TeacherLayout active="classes">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/class-details', { state: backState })}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"
          >
            <ArrowBackRounded sx={{ fontSize: 18 }} /> Back to class
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goWithStudent('/ask-coach')}
              className="inline-flex items-center gap-1 rounded-xl border border-[#E9E6F4] bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:border-[#D9D3EE]"
            >
              <AutoAwesomeRounded sx={{ fontSize: 17 }} /> Ask the curiosity
            </button>
            <button
              type="button"
              onClick={() => goWithStudent('/class-conversation')}
              className="inline-flex items-center gap-1 rounded-xl border border-[#E9E6F4] bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:border-[#D9D3EE]"
            >
              <ChatRounded sx={{ fontSize: 17 }} /> Chats
            </button>
            <button
              type="button"
              onClick={() => flagRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              className="inline-flex items-center gap-1 rounded-xl border border-violet-300 bg-violet-50 px-3.5 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100"
            >
              <FlagRounded sx={{ fontSize: 17 }} /> Reinforce a topic
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={exporting}
              className="inline-flex items-center gap-1 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
            >
              <FileDownloadRounded sx={{ fontSize: 17 }} /> {exporting ? 'Preparing…' : 'Export'}
            </button>
          </div>
        </div>

        {/* Hero: who they are + one plain sentence + understanding gauge */}
        <div className="rounded-3xl bg-white px-7 py-6 shadow-lg shadow-slate-200/60">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex flex-1 items-center gap-4">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-100 text-xl font-bold text-violet-700">
                {avatarInitials(student.first_name)}
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {student.first_name}
                  </h1>
                  <span className={`rounded-full px-2.5 py-0.5 text-[0.7rem] font-bold ${catMeta.tone}`}>
                    {catMeta.flag}
                  </span>
                </div>
                <p className="text-sm text-slate-500">Roll No. {student.roll_number}</p>
                <p className="mt-2 text-[0.95rem] font-medium leading-snug text-slate-700">
                  {catMeta.headline(student.first_name)}
                </p>
              </div>
            </div>
            <Gauge pct={overallScore} color={catMeta.ring} label="overall" />
          </div>

          {/* How the overall score is made up */}
          {evaluation.parts.length > 0 && (
            <div className="mt-5 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-3">
              {evaluation.parts.map((p) => (
                <ScoreBar key={p.key} label={p.label} value={p.value} color={catMeta.ring} />
              ))}
            </div>
          )}
        </div>

        {/* Window selector */}
        <div className="flex items-center justify-end">
          <DateRangeControl value={range} onChange={setRange} includeAllTime />
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <DeltaTile
            icon={<ScheduleRounded sx={{ fontSize: 20 }} />}
            tint="bg-violet-100 text-violet-700"
            label="Time on app"
            sub="Active minutes"
            value={formatMinutes(totalMinutes)}
            up={trend.minUp}
          />
          <DeltaTile
            icon={<ChatRounded sx={{ fontSize: 20 }} />}
            tint="bg-sky-100 text-sky-700"
            label="Chats"
            sub="Total messages"
            value={String(totalMessages)}
            up={trend.msgUp}
          />
          <DeltaTile
            icon={<CheckCircleRounded sx={{ fontSize: 20 }} />}
            tint="bg-emerald-100 text-emerald-700"
            label="Topics explored"
            value={String((reportStudent?.topics ?? []).length)}
          />
          <DeltaTile
            icon={<TrackChangesRounded sx={{ fontSize: 20 }} />}
            tint="bg-amber-100 text-amber-700"
            label="Needs help"
            value={String(weakTopics.length)}
          />
        </div>

        {/* Topics explored · Progress over time · Topic mastery */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Topics explored — grouped by subject, coloured by source */}
          <Card title="Topics explored" sub="By subject · curriculum vs. free chat">
            {topicsBySubject.length === 0 ? (
              <Empty>Topics show up here as {student.first_name} chats.</Empty>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-4 text-xs font-medium text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-violet-300" /> From the curriculum
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-sky-300" /> Free chat
                  </span>
                </div>
                <div className="space-y-3">
                  {topicsBySubject.map(([subject, items]) => (
                    <div key={subject}>
                      <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        {subject}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {items.map((t) => (
                          <span
                            key={`${subject}:${t.topic}`}
                            className={`rounded-full px-3 py-1 text-sm font-medium ${
                              t.from_curriculum ? 'bg-violet-50 text-violet-800' : 'bg-sky-50 text-sky-800'
                            }`}
                          >
                            {t.topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          {/* Progress over time — active minutes per day */}
          <Card title="Progress over time" sub="Active minutes per day">
            {dailySorted.length === 0 ? (
              <Empty>Fills in as {student.first_name} uses the app.</Empty>
            ) : (
              <MiniAreaChart
                points={dailySorted.map((d) => ({ label: formatWeekLabel(String(d.day)), value: d.minutes_spent ?? 0 }))}
              />
            )}
          </Card>

          {/* Topic mastery */}
          <Card title="Topic mastery" sub="Understanding level">
            <div className="flex flex-col items-center gap-3">
              <MasteryDonut mix={masteryMix} pct={understandingPct} />
              <div className="w-full space-y-1.5 text-sm">
                <MasteryRow color="#10b981" label="Understood" n={masteryMix.understood} total={(reportStudent?.topics ?? []).length} />
                <MasteryRow color="#f59e0b" label="Almost there" n={masteryMix.almost} total={(reportStudent?.topics ?? []).length} />
                <MasteryRow color="#fb7185" label="Needs practice" n={masteryMix.needs} total={(reportStudent?.topics ?? []).length} />
              </div>
            </div>
          </Card>
        </div>

        {/* Going well / Needs help — two clear columns */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card title="Going well" accent="emerald">
            {strengths.length === 0 ? (
              <Empty>Shows up here once a topic is solid.</Empty>
            ) : (
              <div className="flex flex-wrap gap-2">
                {strengths.slice(0, 8).map((t) => (
                  <span
                    key={`${t.subject ?? ''}:${t.topic}`}
                    className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800"
                  >
                    ✓ {t.topic}
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card title="Needs help" accent="amber">
            {weakTopics.length === 0 && (reportStudent?.gaps?.length ?? 0) === 0 ? (
              <Empty>Nothing flagged right now.</Empty>
            ) : (
              <div className="space-y-2">
                {weakTopics.slice(0, 6).map((t) => (
                  <div key={`${t.subject ?? ''}:${t.topic}`} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-sm text-slate-700">
                      {t.topic}
                      {t.subject ? (
                        <span className="text-slate-400"> · {SUBJECT_LABELS[t.subject] || t.subject}</span>
                      ) : null}
                    </span>
                    <span className={`flex-none rounded-full px-2 py-0.5 text-[0.68rem] font-bold ${topicStateMeta(t.state).tone}`}>
                      {topicStateMeta(t.state).label}
                    </span>
                  </div>
                ))}
                {(reportStudent?.gaps ?? []).slice(0, 3).map((g) => (
                  <div key={g} className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="text-amber-500">•</span> {g}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Topic by topic — every topic: understanding, depth, and quiz score */}
        <Card
          title="Topic by topic"
          sub="Where they stand on each topic — understanding, how deep they went, and the quiz score"
        >
          {topicRowsBySubject.length === 0 ? (
            <Empty>Fills in as {student.first_name} chats about each topic.</Empty>
          ) : (
            <div className="space-y-4">
              {topicRowsBySubject.map(([subject, rows]) => (
                <div key={subject}>
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    {subject}
                  </p>
                  {rows.map((t) => (
                    <div
                      key={t.topic}
                      className="flex items-center gap-3 border-b border-[#F1EFFA] py-2 last:border-0"
                    >
                      <span className="flex-1 truncate text-sm text-slate-700">{t.topic}</span>
                      <Dots state={t.state} depth={t.depth} />
                      <QuizChip score={t.quiz} />
                      <span
                        className={`flex-none rounded-full px-2 py-0.5 text-[0.68rem] font-bold ${topicStateMeta(t.state).tone}`}
                      >
                        {topicStateMeta(t.state).label}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Reinforce with the curiosity — flag a topic for the coach (merged in) */}
        <div ref={flagRef}>
          <Card
            title="🎯 Reinforce with the curiosity"
            sub={`Flag a topic and the coach will bring it up next time ${student.first_name} chats`}
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                value={flagTopic}
                onChange={(e) => setFlagTopic(e.target.value)}
                className="flex-1 rounded-xl border border-[#E9E6F4] bg-white px-3 py-2.5 text-sm text-slate-700"
              >
                <option value="">Pick a topic…</option>
                {flagTopicOptions.map((t) => (
                  <option key={t.topic} value={t.topic}>
                    {t.topic} — {topicStateMeta(t.state).label}
                  </option>
                ))}
                <option value="__custom__">Something else…</option>
              </select>
              {usingCustomTopic && (
                <input
                  value={flagCustomTopic}
                  onChange={(e) => setFlagCustomTopic(e.target.value)}
                  placeholder="Type a topic"
                  className="flex-1 rounded-xl border border-[#E9E6F4] px-3 py-2.5 text-sm"
                />
              )}
            </div>
            <input
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              placeholder="Add a note for the coach (optional)"
              className="mt-2 w-full rounded-xl border border-[#E9E6F4] px-3 py-2.5 text-sm"
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleFlagSubmit}
                disabled={flagSubmitting || !effectiveTopic}
                className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {flagSubmitting ? 'Flagging…' : 'Flag for reinforcement'}
              </button>
              {flagError ? <span className="text-sm text-rose-600">{flagError}</span> : null}
            </div>
            {flagConfirm ? (
              <p className="mt-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{flagConfirm}</p>
            ) : null}
            {flags.length > 0 && (
              <div className="mt-4 border-t border-[#F1EFFA] pt-3">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  Reinforcements
                </p>
                <div className="flex flex-col gap-2">
                  {/* A finished flag STAYS on the list, marked done. It used to
                      vanish the moment it was cleared, so the teacher could not
                      tell "she worked through it" from "someone removed it". */}
                  {flags.map((f) => {
                    const done = f.status === 'done';
                    return (
                      <div
                        key={f.id}
                        className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                          done ? 'border-emerald-200 bg-emerald-50/60' : 'border-[#E9E6F4]'
                        }`}
                      >
                        <span className={`flex-1 text-sm ${done ? 'text-emerald-800' : 'text-slate-700'}`}>
                          {done ? '✓ ' : ''}
                          {f.topic}
                          {f.note ? <span className="text-slate-400"> — {f.note}</span> : null}
                          {done ? (
                            <span className="ml-1 text-emerald-600">
                              {/* A quiz score is the stronger evidence, so say so when
                                  there is one; otherwise the child worked through it
                                  with the coach, which also counts as done. */}
                              {typeof f.completion_quiz_score === 'number'
                                ? `— done · quiz ${f.completion_quiz_score}%`
                                : '— done · worked through it'}
                              {f.completed_at
                                ? ` · ${new Date(f.completed_at).toLocaleDateString(undefined, {
                                    day: 'numeric',
                                    month: 'short',
                                  })}`
                                : ''}
                            </span>
                          ) : null}
                        </span>
                        {done ? null : (
                          <button
                            type="button"
                            onClick={() => handleClearFlag(f.id)}
                            className="text-lg leading-none text-slate-400 hover:text-rose-600"
                            title="Clear"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        </div>


      </div>
    </TeacherLayout>
  );
};

// ---------------------------------------------------------------------------
// Visuals
// ---------------------------------------------------------------------------

/** Circular understanding gauge. Shows "—" ring when there's no data yet. */
const DeltaTile: React.FC<{
  icon: React.ReactNode;
  tint: string;
  label: string;
  sub?: string;
  value: string;
  up?: boolean | null;
}> = ({ icon, tint, label, sub, value, up }) => (
  <div className="rounded-2xl border border-[#E9E6F4] bg-white px-4 py-4">
    <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${tint}`}>{icon}</span>
    <p className="mt-2.5 text-2xl font-bold tracking-tight text-slate-900">{value}</p>
    <p className="text-xs font-medium text-slate-500">
      {label}
      {sub ? <span className="text-slate-400"> · {sub}</span> : null}
    </p>
    {up != null && (
      <p className={`mt-1 inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
        {up ? <ArrowUpwardRounded sx={{ fontSize: 13 }} /> : <ArrowDownwardRounded sx={{ fontSize: 13 }} />}
        vs last 7d
      </p>
    )}
  </div>
);

const MiniAreaChart: React.FC<{ points: { label: string; value: number }[] }> = ({ points }) => {
  const W = 260, H = 120, pad = 8;
  const max = Math.max(1, ...points.map((p) => p.value));
  const n = points.length;
  const x = (i: number) => (n <= 1 ? W / 2 : pad + (i / (n - 1)) * (W - 2 * pad));
  const y = (v: number) => H - pad - (v / max) * (H - 2 * pad);
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L ${x(n - 1).toFixed(1)} ${H - pad} L ${x(0).toFixed(1)} ${H - pad} Z`;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-32 w-full">
        <path d={area} fill="#ede9fe" />
        <path d={line} fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.value)} r="2.5" fill="#7c3aed" />
        ))}
      </svg>
      {n > 1 && (
        <div className="mt-1 flex justify-between text-[10px] text-slate-400">
          <span>{points[0].label}</span>
          <span>{points[n - 1].label}</span>
        </div>
      )}
    </div>
  );
};

const MASTERY_SR = [
  { key: 'understood', color: '#10b981' },
  { key: 'almost', color: '#f59e0b' },
  { key: 'needs', color: '#fb7185' },
] as const;

const MasteryDonut: React.FC<{ mix: { understood: number; almost: number; needs: number }; pct: number | null }> = ({ mix, pct }) => {
  const total = mix.understood + mix.almost + mix.needs;
  const r = 34, c = 2 * Math.PI * r, sw = 12;
  let off = 0;
  return (
    <svg viewBox="0 0 90 90" className="h-28 w-28">
      <circle cx="45" cy="45" r={r} fill="none" stroke="#eef1f6" strokeWidth={sw} />
      {total > 0 &&
        MASTERY_SR.map((s) => {
          const v = mix[s.key];
          if (!v) return null;
          const len = (v / total) * c;
          const el = (
            <circle
              key={s.key}
              cx="45"
              cy="45"
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={sw}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-off}
              transform="rotate(-90 45 45)"
            />
          );
          off += len;
          return el;
        })}
      <text x="45" y="43" textAnchor="middle" fontSize="16" fontWeight="700" fill="#0f172a">
        {pct != null ? `${pct}%` : '—'}
      </text>
      <text x="45" y="56" textAnchor="middle" fontSize="7.5" fill="#94a3b8">
        understood
      </text>
    </svg>
  );
};

const MasteryRow: React.FC<{ color: string; label: string; n: number; total: number }> = ({ color, label, n, total }) => {
  const pct = total > 0 ? Math.round((n / total) * 100) : 0;
  return (
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-2 text-slate-600">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
        {label}
      </span>
      <span className="font-semibold text-slate-500">{pct}%</span>
    </div>
  );
};

const Gauge: React.FC<{ pct: number | null; color: string; label?: string }> = ({
  pct,
  color,
  label = 'understood',
}) => {
  const r = 40;
  const c = 2 * Math.PI * r;
  const filled = pct != null ? (Math.max(0, Math.min(100, pct)) / 100) * c : 0;
  return (
    <div className="flex flex-none flex-col items-center">
      <svg viewBox="0 0 100 100" className="h-24 w-24">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#eef1f6" strokeWidth="10" />
        {pct != null && (
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${filled} ${c - filled}`}
            transform="rotate(-90 50 50)"
          />
        )}
        <text x="50" y="49" textAnchor="middle" fontSize="22" fontWeight="700" fill="#0f172a">
          {pct != null ? `${pct}%` : '—'}
        </text>
        <text x="50" y="64" textAnchor="middle" fontSize="9" fill="#94a3b8">
          {label}
        </text>
      </svg>
    </div>
  );
};

/** One component of the overall score: a label, its 0–100 value, and a bar. */
const ScoreBar: React.FC<{ label: string; value: number; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div>
    <div className="flex items-center justify-between text-xs">
      <span className="font-medium text-slate-500">{label}</span>
      <span className="font-bold text-slate-800">{Math.round(value)}</span>
    </div>
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div
        className="h-full rounded-full"
        style={{ width: `${Math.max(0, Math.min(100, value))}%`, backgroundColor: color }}
      />
    </div>
  </div>
);

/** Depth indicator — how deep the conversations on a topic went (1–3 dots). */
const Dots: React.FC<{ state: ReinforcementTopicState; depth: number | null }> = ({ state, depth }) => {
  const n =
    depth != null
      ? Math.max(1, Math.min(3, Math.round(depth)))
      : state === 'solid'
        ? 3
        : state === 'partly_solid'
          ? 2
          : 1;
  return (
    <span className="flex flex-none gap-1">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`h-1.5 w-1.5 rounded-full ${i < n ? 'bg-violet-500' : 'bg-slate-200'}`} />
      ))}
    </span>
  );
};

/** Quiz-score pill for a topic. Muted "—" placeholder when no quiz taken yet. */
const QuizChip: React.FC<{ score: number | null }> = ({ score }) => {
  if (score == null) {
    return (
      <span
        className="flex-none rounded-full bg-slate-50 px-2 py-0.5 text-[0.68rem] font-medium text-slate-400"
        title="No quiz taken on this topic yet"
      >
        Quiz —
      </span>
    );
  }
  // Pass threshold is 50 (see backend student_progress.PASS_THRESHOLD).
  const tone =
    score >= 75
      ? 'bg-emerald-100 text-emerald-700'
      : score >= 50
        ? 'bg-amber-100 text-amber-700'
        : 'bg-rose-100 text-rose-700';
  return (
    <span className={`flex-none rounded-full px-2 py-0.5 text-[0.68rem] font-bold ${tone}`}>
      Quiz {score}
    </span>
  );
};

/** Tiny bar chart of daily activity. */
const ACCENT: Record<string, string> = {
  emerald: 'text-emerald-600',
  amber: 'text-amber-600',
};

const Card: React.FC<{
  title: string;
  sub?: string;
  accent?: 'emerald' | 'amber';
  children: React.ReactNode;
}> = ({ title, sub, accent, children }) => (
  <section className="rounded-2xl border border-[#E9E6F4] bg-white px-5 py-4 shadow-sm">
    <div className="mb-3">
      <h2 className={`text-base font-bold ${accent ? ACCENT[accent] : 'text-slate-900'}`}>{title}</h2>
      {sub ? <p className="text-xs text-slate-500">{sub}</p> : null}
    </div>
    {children}
  </section>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-500">{children}</p>
);

export default StudentReport;
